import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CalendarPlus, Check, MoveRight, SkipForward, Square } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { formatDate } from "../../utils/appraisal";
import { DueLine, ProgressPicker, ProgressPill, type AgreedObjective, type Progress } from "./Objectives";

interface CheckIn { id: number; due_date: string; status: string }

interface Plan {
  review_id: number;
  employee_id: number;
  employee_name: string;
  cadence: string;
  cadence_label: string;
  minutes: number;
  time: string | null;
  next: CheckIn | null;
  overdue: boolean;
  last_done: string | null;
  held: number;
  objectives: AgreedObjective[];
}

function dayText(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 0) return `${formatDate(iso)} (${-diff} day${diff === -1 ? "" : "s"} ago)`;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

/**
 * My team: the short catch-ups agreed in each appraisal. One row per person,
 * with the next catch-up and quick actions. Holding one (Done) plans the next.
 */
export default function CheckInsCard({ readOnly = false }: { readOnly?: boolean }) {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [agenda, setAgenda] = useState<string[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [doneFor, setDoneFor] = useState<Plan | null>(null);
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState<Record<number, Progress>>({});
  const [moving, setMoving] = useState<Plan | null>(null);
  const [moveTo, setMoveTo] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ plans: Plan[]; agenda: string[] }>("/api/check-ins");
      setPlans(res.plans);
      setAgenda(res.agenda);
    } catch {
      setPlans([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const act = async (plan: Plan, fn: () => Promise<unknown>) => {
    setBusy(plan.review_id);
    setError("");
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That didn't work. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  if (!plans || plans.length === 0) return null;

  return (
    <section className="card p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-pme-navy">
        <span className="text-pme-blue"><CalendarClock size={16} strokeWidth={1.75} aria-hidden /></span>
        Catch-ups
      </h2>
      <p className="mt-0.5 text-xs text-gray-500">
        Short catch-ups agreed in each appraisal. Keep them to {Math.min(...plans.map((p) => p.minutes))} minutes or so:
        how things are going, progress on objectives, anything blocking them, one thing to focus on.
      </p>
      {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}

      <ul className="mt-2 space-y-2">
        {plans.map((p) => (
          <li key={p.review_id} className={`rounded-lg border p-3 ${p.overdue ? "border-amber-200 bg-amber-50/60" : "border-gray-100 bg-gray-50/60"}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{p.employee_name}</p>
                <p className="text-xs text-gray-500">
                  {p.cadence_label.charAt(0).toUpperCase() + p.cadence_label.slice(1)}, {p.minutes} min
                  {p.held > 0 ? ` · ${p.held} held` : ""}
                </p>
              </div>
              {p.next && (
                <p className={`text-xs font-semibold ${p.overdue ? "text-amber-800" : "text-gray-700"}`}>
                  Next: {dayText(p.next.due_date)}{p.time ? `, ${p.time}` : ""}
                </p>
              )}
            </div>
            {p.objectives.length > 0 && (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[11px] font-semibold text-gray-500">
                  Their objectives: {p.objectives.filter((o) => o.progress === "done").length} of {p.objectives.length} done
                  {p.objectives.some((o) => o.progress === "need_help") ? " · needs help" : ""}
                </summary>
                <ul className="mt-1 space-y-1 text-xs text-gray-700">
                  {p.objectives.map((o) => (
                    <li key={o.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className={o.progress === "done" ? "line-through text-gray-500" : ""}>{o.objective}</span>
                      <ProgressPill progress={o.progress} />
                      <span className="text-[11px]"><DueLine due={o.due_date} done={o.progress === "done"} /></span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {!readOnly && p.next && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" disabled={busy === p.review_id} onClick={() => { setDoneFor(p); setNote(""); setProgress(Object.fromEntries(p.objectives.map((o) => [o.id, o.progress]))); }}
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg bg-green-600 px-3 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-60">
                  <Check size={14} aria-hidden /> Done
                </button>
                <button type="button" disabled={busy === p.review_id} onClick={() => { setMoving(p); setMoveTo(p.next?.due_date ?? ""); }}
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  <MoveRight size={14} aria-hidden /> Move
                </button>
                <button type="button" disabled={busy === p.review_id}
                  onClick={() => void act(p, () => api.post(`/api/check-ins/${p.next!.id}/skip`))}
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  <SkipForward size={14} aria-hidden /> Skip this one
                </button>
                <a href={`/api/check-ins/plans/${p.review_id}/series.ics`}
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  <CalendarPlus size={14} aria-hidden /> Add to my calendar
                </a>
                <button type="button" disabled={busy === p.review_id}
                  onClick={() => { if (window.confirm(`Stop the regular catch-ups with ${p.employee_name}?`)) void act(p, () => api.post(`/api/check-ins/plans/${p.review_id}/stop`)); }}
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 text-xs font-medium text-gray-500 hover:text-red-600">
                  <Square size={12} aria-hidden /> Stop
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {doneFor && doneFor.next && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={() => setDoneFor(null)}>
          <div role="dialog" aria-modal="true" className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-900">Catch-up with {doneFor.employee_name}</h3>
            <ol className="mt-2 list-decimal space-y-0.5 pl-4 text-xs text-gray-600">
              {agenda.map((a) => <li key={a}>{a}</li>)}
            </ol>
            {doneFor.objectives.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-700">Progress on their objectives</p>
                <p className="text-[11px] text-gray-500">{doneFor.employee_name.split(" ")[0]} sees this on their Performance page.</p>
                <ul className="mt-1.5 space-y-2">
                  {doneFor.objectives.map((o) => (
                    <li key={o.id} className="rounded-lg bg-gray-50 px-2.5 py-2">
                      <p className="text-xs text-gray-800">{o.objective}</p>
                      {o.progress_note && <p className="mt-0.5 text-[11px] text-gray-500">Their latest: {o.progress_note}</p>}
                      <div className="mt-1"><ProgressPicker value={progress[o.id] ?? null} onChange={(v) => setProgress((cur) => ({ ...cur, [o.id]: v }))} /></div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <label className="mt-3 block text-xs">
              <span className="font-medium text-gray-700">Quick note (optional, only you and HR see it)</span>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="input mt-1 w-full"
                placeholder="e.g. On track with the stock count objective. Needs Excel training booked." />
            </label>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setDoneFor(null)} className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600">Cancel</button>
              <button type="button" onClick={() => {
                const p = doneFor; setDoneFor(null);
                const changed = p.objectives.filter((o) => (progress[o.id] ?? null) !== (o.progress ?? null));
                void act(p, async () => {
                  for (const o of changed) {
                    await api.put(`/api/objectives/${o.id}/progress`, { progress: progress[o.id] ?? null });
                  }
                  await api.post(`/api/check-ins/${p.next!.id}/done`, { note });
                });
              }} className="flex-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white">Mark as done</button>
            </div>
          </div>
        </div>
      )}

      {moving && moving.next && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={() => setMoving(null)}>
          <div role="dialog" aria-modal="true" className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-900">Move the catch-up with {moving.employee_name}</h3>
            <label className="mt-3 block text-xs">
              <span className="font-medium text-gray-700">New date</span>
              <input type="date" value={moveTo} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setMoveTo(e.target.value)} className="input mt-1 w-full" />
            </label>
            <p className="mt-1 text-[11px] text-gray-500">This moves the reminder. Move it in your calendar too.</p>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setMoving(null)} className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600">Cancel</button>
              <button type="button" disabled={!moveTo} onClick={() => {
                const p = moving; setMoving(null);
                void act(p, () => api.put(`/api/check-ins/${p.next!.id}`, { due_date: moveTo }));
              }} className="flex-1 rounded-lg bg-pme-blue px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">Move it</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
